import { trim, trimStart } from "lodash";
export function buildRlPurl(projectName, packageName, version, reportType = "version") {
    return `pkg:rl/${projectName}/${packageName}${version ? `@${version}` : ""}${reportType === "repro" ? "?build=repro" : ""}`;
}
function validateQualifierKey(key) {
    const startsWith = key.charCodeAt(0);
    if (!((startsWith >= 65 && startsWith <= 90) || // A-Z
        (startsWith >= 97 && startsWith <= 122) // a-z
    )) {
        throw new Error("Qualifier key must start with an ASCII letter");
    }
    // The key must be composed only of ASCII letters and numbers,
    // '.', '-' and '_' (period, dash and underscore).
    for (const character of key) {
        const code = character.charCodeAt(0);
        if (!((code >= 48 && code <= 57) || // 0-9
            (code >= 65 && code <= 90) || // A-Z
            (code >= 97 && code <= 122) || // a-z
            code === 46 || // .
            code === 45 || // -
            code === 95 // _
        )) {
            throw new Error(`Qualifier key, "${key}",  contains an illegal character`);
        }
    }
}
function validatePurlType(packageType) {
    if (!packageType) {
        throw new Error("Type is required");
    }
    const typeStart = packageType.charCodeAt(0);
    if (!((typeStart >= 65 && typeStart <= 90) || // A-Z
        (typeStart >= 97 && typeStart <= 122) // a-z
    )) {
        throw new Error("Type must start with an ASCII letter");
    }
    for (const character of packageType) {
        const code = character.charCodeAt(0);
        if (!((code >= 48 && code <= 57) || // 0-9
            (code >= 65 && code <= 90) || // A-Z
            (code >= 97 && code <= 122) || // a-z
            code === 46 || // .
            code === 43 || // +
            code === 45 // -
        )) {
            throw new Error(`type contains an illegal character: ${code}`);
        }
    }
}
// Combines official types https://github.com/package-url/purl-spec/tree/main/types-doc
// With TC specific ones (internal TC repo - core/src/purl/purl_types.cpp)
var PURL_TYPE;
(function (PURL_TYPE) {
    PURL_TYPE["alpm_k"] = "alpm";
    PURL_TYPE["apk_k"] = "apk";
    PURL_TYPE["bazel_k"] = "bazel";
    PURL_TYPE["bitbucket_k"] = "bitbucket";
    PURL_TYPE["bitnami_k"] = "bitnami";
    PURL_TYPE["cargo_k"] = "cargo";
    PURL_TYPE["cocoapods_k"] = "cocoapods";
    PURL_TYPE["composer_k"] = "composer";
    PURL_TYPE["conan_k"] = "conan";
    PURL_TYPE["conda_k"] = "conda";
    PURL_TYPE["cpan_k"] = "cpan";
    PURL_TYPE["cran_k"] = "cran";
    PURL_TYPE["deb_k"] = "deb";
    PURL_TYPE["docker_k"] = "docker";
    PURL_TYPE["gem_k"] = "gem";
    PURL_TYPE["generic_k"] = "generic";
    PURL_TYPE["github_k"] = "github";
    PURL_TYPE["gitlab_k"] = "gitlab";
    PURL_TYPE["golang_k"] = "golang";
    PURL_TYPE["hackage_k"] = "hackage";
    PURL_TYPE["hex_k"] = "hex";
    PURL_TYPE["huggingface_k"] = "huggingface";
    PURL_TYPE["julia_k"] = "julia";
    PURL_TYPE["kaniko_k"] = "kaniko";
    PURL_TYPE["luarocks_k"] = "luarocks";
    PURL_TYPE["lxd_k"] = "lxd";
    PURL_TYPE["maven_k"] = "maven";
    PURL_TYPE["mlflow_k"] = "mlflow";
    PURL_TYPE["npm_k"] = "npm";
    PURL_TYPE["nuget_k"] = "nuget";
    PURL_TYPE["oci_k"] = "oci";
    PURL_TYPE["otp_k"] = "otp";
    PURL_TYPE["psgallery_k"] = "psgallery";
    PURL_TYPE["pub_k"] = "pub";
    PURL_TYPE["pypi_k"] = "pypi";
    PURL_TYPE["qpkg_k"] = "qpkg";
    PURL_TYPE["rl_k"] = "rl";
    PURL_TYPE["rpm_k"] = "rpm";
    PURL_TYPE["swid_k"] = "swid";
    PURL_TYPE["swift_k"] = "swift";
    PURL_TYPE["vsx_k"] = "vsx";
    PURL_TYPE["yocto_k"] = "yocto";
})(PURL_TYPE || (PURL_TYPE = {}));
function normalizeNamespace(purl) {
    var _a;
    switch (purl.type) {
        case PURL_TYPE.alpm_k:
        case PURL_TYPE.apk_k:
        case PURL_TYPE.bitbucket_k:
        case PURL_TYPE.composer_k:
        case PURL_TYPE.deb_k:
        case PURL_TYPE.github_k:
        case PURL_TYPE.golang_k:
        case PURL_TYPE.hex_k:
        case PURL_TYPE.npm_k:
        case PURL_TYPE.vsx_k:
            return (_a = purl.namespace) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        default:
            return purl.namespace;
    }
}
function normalizeName(purl) {
    var _a, _b;
    let name = purl.name;
    if (purl.type === PURL_TYPE.pub_k) {
        name = name.replaceAll("-", "_");
    }
    else if (purl.type === PURL_TYPE.pypi_k) {
        name = name.replaceAll("_", "-");
        name = name.replaceAll(".", "-");
    }
    switch (purl.type) {
        case PURL_TYPE.alpm_k:
        case PURL_TYPE.apk_k:
        case PURL_TYPE.bitbucket_k:
        case PURL_TYPE.bitnami_k:
        case PURL_TYPE.composer_k:
        case PURL_TYPE.deb_k:
        case PURL_TYPE.gitlab_k:
        case PURL_TYPE.github_k:
        case PURL_TYPE.golang_k:
        case PURL_TYPE.hex_k:
        case PURL_TYPE.nuget_k:
        case PURL_TYPE.oci_k:
        case PURL_TYPE.psgallery_k:
        case PURL_TYPE.pub_k:
        case PURL_TYPE.pypi_k:
        case PURL_TYPE.vsx_k:
            return name.toLowerCase();
        case PURL_TYPE.mlflow_k:
            return ((_b = (_a = purl.qualifiers) === null || _a === void 0 ? void 0 : _a.repository_url) === null || _b === void 0 ? void 0 : _b.includes("databricks"))
                ? name.toLowerCase()
                : name;
        default:
            return name;
    }
}
function normalizeVersion(purl) {
    var _a;
    switch (purl.type) {
        case PURL_TYPE.huggingface_k:
        case PURL_TYPE.luarocks_k:
            return (_a = purl.version) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        default:
            return purl.version;
    }
}
function validatePurl(purl) {
    var _a;
    var _b;
    if (!purl.name) {
        throw new Error("Name is required");
    }
    if (!purl.namespace) {
        switch (purl.type) {
            // Namespace is mandatory
            case PURL_TYPE.alpm_k:
            case PURL_TYPE.apk_k:
            case PURL_TYPE.bitbucket_k:
            case PURL_TYPE.composer_k:
            case PURL_TYPE.cpan_k:
            case PURL_TYPE.deb_k:
            case PURL_TYPE.github_k:
            case PURL_TYPE.golang_k:
            case PURL_TYPE.huggingface_k:
            case PURL_TYPE.kaniko_k:
            case PURL_TYPE.maven_k:
            case PURL_TYPE.qpkg_k:
            case PURL_TYPE.rl_k:
            case PURL_TYPE.rpm_k:
            case PURL_TYPE.swift_k:
            case PURL_TYPE.vsx_k:
                throw new Error(`Namespace is required for ${purl.type}`);
        }
    }
    if (purl.namespace) {
        switch (purl.type) {
            // Namespace is prohibited
            case PURL_TYPE.bazel_k:
            case PURL_TYPE.bitnami_k:
            case PURL_TYPE.cargo_k:
            case PURL_TYPE.cocoapods_k:
            case PURL_TYPE.conda_k:
            case PURL_TYPE.cran_k:
            case PURL_TYPE.gem_k:
            case PURL_TYPE.hackage_k:
            case PURL_TYPE.julia_k:
            case PURL_TYPE.mlflow_k:
            case PURL_TYPE.nuget_k:
            case PURL_TYPE.oci_k:
            case PURL_TYPE.otp_k:
            case PURL_TYPE.pub_k:
            case PURL_TYPE.pypi_k:
                throw new Error(`Namespace is prohibited for ${purl.type}`);
        }
    }
    if (purl.type === PURL_TYPE.swift_k) {
        if (!purl.name) {
            throw new Error(`Name is required for ${purl.type}`);
        }
        if (!purl.version) {
            throw new Error(`Version is required for ${purl.type}`);
        }
    }
    if (purl.type === PURL_TYPE.conan_k) {
        if (!purl.namespace) {
            if ((_a = purl.qualifiers) === null || _a === void 0 ? void 0 : _a.channel) {
                throw new Error(`Namespace is required when "channel" qualifier is present for ${purl.type}`);
            }
        }
        else if (Object.keys((_b = purl.qualifiers) !== null && _b !== void 0 ? _b : {}).length === 0) {
            throw new Error(`"qualifiers" are required when namespace is present for ${purl.type}`);
        }
    }
    if (purl.qualifiers) {
        Object.keys(purl.qualifiers).forEach((key) => validateQualifierKey(key));
    }
}
/**
 * Function for parsing and validating PURL
 * PURL specification: https://github.com/package-url/purl-spec
 *
 * Throws an error if purl is invalid
 *
 * @param purl String to be parsed
 * @returns PURL
 * */
export function parsePurl(purl) {
    let toBeParsed = purl;
    try {
        new URL(toBeParsed);
    }
    catch (_a) {
        throw new Error("PURL is not a valid URL");
    }
    const colonDelimiterIndex = toBeParsed.indexOf(":");
    const scheme = toBeParsed.substring(0, colonDelimiterIndex + 1);
    if (scheme.toLowerCase() !== "pkg:") {
        throw new Error(`Invalid scheme: ${scheme}`);
    }
    toBeParsed = trimStart(toBeParsed.substring(colonDelimiterIndex + 1), "/");
    const slashDelimiterIndex = toBeParsed.indexOf("/");
    const packageType = toBeParsed
        .substring(0, slashDelimiterIndex)
        .toLowerCase();
    toBeParsed = toBeParsed.substring(slashDelimiterIndex + 1);
    validatePurlType(packageType);
    const subpathDelimiterIndex = toBeParsed.indexOf("#");
    let subpath;
    if (subpathDelimiterIndex !== -1) {
        subpath = trim(toBeParsed.substring(subpathDelimiterIndex + 1), "/");
        toBeParsed = toBeParsed.substring(0, subpathDelimiterIndex);
    }
    const qualifiersDelimiterIndex = toBeParsed.indexOf("?");
    let qualifiers;
    if (qualifiersDelimiterIndex !== -1) {
        qualifiers = Object.fromEntries(new URLSearchParams(toBeParsed.substring(qualifiersDelimiterIndex + 1))
            .entries()
            .map(([key, value]) => [key.toLowerCase(), value]));
        toBeParsed = toBeParsed.substring(0, qualifiersDelimiterIndex);
    }
    const versionDelimiterIndex = toBeParsed.indexOf("@");
    let version;
    if (versionDelimiterIndex !== -1) {
        version = decodeURIComponent(toBeParsed.substring(versionDelimiterIndex + 1));
        toBeParsed = toBeParsed.substring(0, versionDelimiterIndex);
    }
    let rawName, rawNamespace;
    const lastSlashIndex = toBeParsed.lastIndexOf("/");
    if (lastSlashIndex === -1) {
        rawName = decodeURIComponent(toBeParsed.substring(lastSlashIndex + 1));
    }
    else {
        rawName = decodeURIComponent(toBeParsed.substring(lastSlashIndex + 1));
        rawNamespace = decodeURIComponent(toBeParsed.substring(0, lastSlashIndex));
    }
    const parsedPurl = {
        type: packageType,
        namespace: trim(rawNamespace, "/")
            .split("/")
            .filter((part) => part !== "")
            .join("/"),
        name: trim(rawName, "/"),
        version,
        qualifiers,
        subpath,
    };
    parsedPurl.namespace = normalizeNamespace(parsedPurl);
    parsedPurl.name = normalizeName(parsedPurl);
    parsedPurl.version = normalizeVersion(parsedPurl);
    validatePurl(parsedPurl);
    return parsedPurl;
}
//# sourceMappingURL=purl.js.map